import { useState, type ReactNode } from "react";
import { CopyIcon, CheckIcon } from "./icons";

// A read-only value (generated passphrase, recovery code, or CLI command) sitting
// in a box with trailing icon buttons: `[ (prefix)value ] (actions…) (copy)`.
// `user-select: all` on the box makes one-click manual selection the fallback if
// the clipboard write is blocked, so a copy failure stays non-blocking. `prefix`
// (e.g. a `$` shell prompt) is shown but excluded from the copied text; `variant`
// swaps the box styling (secret vs. terminal command).
export function CopyableSecret({
  value,
  actions,
  prefix,
  variant,
}: {
  value: string;
  actions?: ReactNode;
  prefix?: ReactNode;
  variant?: "command";
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* non-blocking: the value is visible and selectable in the box */
    }
  }
  return (
    <div className={variant === "command" ? "secret-row secret-row-command" : "secret-row"}>
      <div className="secret-box">
        {prefix}
        {value}
      </div>
      {actions}
      <button
        type="button"
        className="icon-button"
        onClick={copy}
        aria-label={copied ? "Copied to clipboard" : "Copy to clipboard"}
        title={copied ? "Copied" : "Copy to clipboard"}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
    </div>
  );
}

export default CopyableSecret;
